import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, ImagePlus, Pencil, Plus, Power, RefreshCw, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const EMPTY = {
  name: "",
  type: "cashapp",
  provider: "manual",
  purpose: "",
  auto_payout_enabled: false,
  account_label: "",
  account_value: "",
  qr_code_url: "",
  instructions: "",
  is_active: true,
};

export default function AdminPaymentMethods() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/payment-methods");
    setRows(data);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/payment-methods/${editingId}`, form);
        toast.success("Payment method updated");
      } else {
        await api.post("/admin/payment-methods", form);
        toast.success("Payment method added");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const edit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      type: row.type || "manual",
      provider: row.provider || "manual",
      purpose: row.purpose || "",
      auto_payout_enabled: !!row.auto_payout_enabled,
      account_label: row.account_label || "",
      account_value: row.account_value || "",
      qr_code_url: row.qr_code_url || "",
      instructions: row.instructions || "",
      is_active: row.is_active !== false,
    });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const startNew = () => {
    setEditingId(null);
    setForm(EMPTY);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.name}?`)) return;
    try {
      await api.delete(`/admin/payment-methods/${row.id}`);
      toast.success("Payment method deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/admin/payment-methods/${row.id}/toggle`, { is_active: !row.is_active });
      toast.success(`${row.name} turned ${row.is_active ? "off" : "on"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadQr = (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please choose an image file for the QR code");
      return;
    }
    if (file.size > 800 * 1024) {
      toast.error("QR image is too large. Use an image under 800 KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((current) => ({ ...current, qr_code_url: String(reader.result || "") }));
    reader.onerror = () => toast.error("Could not read QR image");
    reader.readAsDataURL(file);
  };

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Payment Methods</h1>
          <p className="mt-1 text-sm text-slate-500">Add payment rails shown to operators and customers.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={startNew} className="inline-flex items-center gap-2 rounded-full bg-[#2F6FA5] px-4 py-2 text-sm font-black text-white shadow hover:bg-[#4E91C8]">
            <Plus className="h-4 w-4" />
            Add method
          </button>
          <button onClick={load} className="rounded-md border border-white/10 p-2 hover:bg-white/5" title="Refresh methods">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[390px_minmax(0,1fr)]">
        <form ref={formRef} onSubmit={save} className="min-w-0 rounded-xl border border-white/10 bg-[#121214] p-4 sm:p-5">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold">
            <Plus className="h-4 w-4 text-[#2F6FA5]" />
            {editingId ? "Edit method" : "New method"}
          </div>
          <div className="space-y-4">
            <Field label="Name">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} placeholder="Cash App, Zelle, Chime" />
            </Field>
            <Field label="Type">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inp}>
                <option value="card">Card</option>
                <option value="cashapp">Cash App</option>
                <option value="cashapp_lightning">Cash App Lightning</option>
                <option value="ecashapp">Cash App (XXPay)</option>
                <option value="zelle">Zelle</option>
                <option value="chime">Chime</option>
                <option value="venmo">Venmo</option>
                <option value="paypal">PayPal</option>
                <option value="xxpayus">XXPay</option>
                <option value="applepay">Apple Pay</option>
                <option value="apple_pay">Apple Pay (PlayHub)</option>
                <option value="googlepay">Google Pay</option>
                <option value="google_pay">Google Pay (PlayHub)</option>
                <option value="crypto">Crypto</option>
                <option value="manual">Manual</option>
              </select>
            </Field>
            <Field label="Provider">
              <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className={inp}>
                <option value="manual">Customer-facing method / manual review</option>
                <option value="playhub">PlayHub checkout</option>
                <option value="nowpayments">NOWPayments crypto checkout</option>
                <option value="xxpay">XXPay checkout</option>
              </select>
            </Field>
            <Field label="Use for">
              <select value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className={inp}>
                <option value="">Cash in only</option>
                <option value="cashin">Cash in only</option>
                <option value="cashout">Cash out only</option>
                <option value="both">Cash in + cash out</option>
              </select>
              <div className="mt-1.5 text-xs leading-relaxed text-slate-500">
                Cash in shows on Add money. Cash out shows on Withdraw money. Use the auto payout checkbox only for the separate auto cashout method.
              </div>
            </Field>
            {isAutoPayoutCapableForm(form) && (
              <label className="flex items-start gap-2 rounded-lg border border-emerald-700/30 bg-emerald-950/20 p-3 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={!!form.auto_payout_enabled}
                  onChange={(e) => setForm({ ...form, auto_payout_enabled: e.target.checked })}
                  className="mt-1"
                />
                <span>
                  <span className="block font-bold text-emerald-300">Auto payout under $200</span>
                  <span className="mt-1 block text-xs leading-relaxed text-slate-400">
                    Turn ON only for the auto cashout row. Leave OFF for manual Chime/Cash App review.
                  </span>
                </span>
              </label>
            )}
            <Field label="Account label">
              <input value={form.account_label} onChange={(e) => setForm({ ...form, account_label: e.target.value })} className={inp} placeholder="$cashtag, email, wallet label" />
            </Field>
            <Field label="Account value">
              <input value={form.account_value} onChange={(e) => setForm({ ...form, account_value: e.target.value })} className={inp} placeholder="$FEASlots or payments@example.com" />
            </Field>
            <Field label="QR code image URL">
              <input
                value={form.qr_code_url}
                onChange={(e) => setForm({ ...form, qr_code_url: e.target.value })}
                className={inp}
                placeholder="Paste Chime/Venmo QR image URL"
              />
              <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#2F6FA5]/30 px-3 py-1.5 text-xs font-bold text-[#2F6FA5] hover:bg-[#2F6FA5]/10">
                <ImagePlus className="h-3.5 w-3.5" />
                Upload QR image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadQr(e.target.files?.[0])}
                />
              </label>
              {form.qr_code_url && (
                <div className="mt-3 rounded-lg border border-white/10 bg-black/25 p-3">
                  <img
                    src={form.qr_code_url}
                    alt="Payment QR preview"
                    className="mx-auto max-h-40 rounded-md bg-white object-contain p-2"
                  />
                  <div className="mt-2 text-xs text-slate-500">This QR will show to customers for manual payment.</div>
                </div>
              )}
            </Field>
            <Field label="Instructions">
              <textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className={`${inp} min-h-24`} placeholder="What user should write in payment note" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active
            </label>
          </div>
          <div className="mt-5 flex gap-2">
            <button disabled={saving} className="flex-1 rounded-full bg-[#2F6FA5] px-4 py-2.5 font-bold text-white hover:bg-[#4E91C8] disabled:opacity-60">
              {saving ? "Saving..." : editingId ? "Save changes" : "Add method"}
            </button>
            {editingId && (
              <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded-full border border-white/10 px-4 py-2.5 text-slate-300 hover:bg-white/5">
                Cancel
              </button>
            )}
          </div>
        </form>

        <div className="min-w-0 overflow-hidden rounded-xl border border-white/10 bg-[#121214]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
            <div>
              <div className="text-sm font-black text-slate-100">Saved methods</div>
              <div className="text-xs text-slate-500">Turn on/off, edit, delete, or add a new method.</div>
            </div>
            <button onClick={startNew} className="inline-flex items-center gap-2 rounded-full border border-[#2F6FA5]/40 bg-[#2F6FA5]/15 px-3 py-1.5 text-xs font-black text-[#6EA8D8] hover:bg-[#2F6FA5]/25">
              <Plus className="h-3.5 w-3.5" />
              Add method
            </button>
          </div>
          <div className="overflow-x-auto">
          <table className="min-w-[760px] w-full text-sm">
            <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-white/5">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-100">{row.name}</div>
                    <div className="text-xs uppercase text-slate-500">
                      {[row.type, row.provider || "manual", paymentPurposeLabel(row.purpose)].filter(Boolean).join(" · ")}
                    </div>
                    {isAutoPayoutCashoutRow(row) && (
                      <div className="mt-1 inline-flex rounded-full border border-emerald-700/40 bg-emerald-900/25 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-300">
                        Auto payout under $200
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs text-slate-300">{row.account_value || "-"}</div>
                    <div className="max-w-sm truncate text-xs text-slate-500">{row.instructions || "-"}</div>
                    {row.qr_code_url && (
                      <a
                        href={row.qr_code_url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-flex text-xs font-semibold text-[#2F6FA5] hover:text-[#6EA8D8]"
                      >
                        QR code saved
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {row.is_active ? (
                      <span className="inline-flex items-center gap-1 rounded-md border border-emerald-700/40 bg-emerald-900/30 px-2 py-0.5 text-xs text-emerald-300"><CheckCircle2 className="h-3 w-3" /> Active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800 px-2 py-0.5 text-xs text-slate-300"><XCircle className="h-3 w-3" /> Hidden</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => toggle(row)}
                      title={row.is_active ? "Turn off" : "Turn on"}
                      className={`mr-1 rounded border p-1.5 hover:bg-white/10 ${
                        row.is_active ? "border-emerald-700/30 text-emerald-300" : "border-white/10 text-slate-400"
                      }`}
                    >
                      <Power className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => edit(row)} className="mr-1 rounded border border-white/10 p-1.5 text-slate-300 hover:bg-white/10"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(row)} className="rounded border border-red-700/30 p-1.5 text-red-300 hover:bg-red-500/15"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-slate-500">No payment methods yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-slate-100 outline-none focus:border-[#2F6FA5]";

function paymentPurposeLabel(value) {
  if (value === "cashout") return "cash out";
  if (value === "both") return "cash in + cash out";
  return "cash in";
}

function methodKey(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function isExplicitAutoPayoutName(row) {
  const name = methodKey(row?.name);
  return name.includes("auto") && !name.includes("manual");
}

function isAutoPayoutCashoutRow(row) {
  if ((row?.provider || "manual") !== "manual") return false;
  if (!["cashout", "both"].includes(methodKey(row?.purpose))) return false;
  if (!row?.auto_payout_enabled) return false;
  if (!isExplicitAutoPayoutName(row)) return false;
  return ["chime", "cashapp", "ecashapp"].includes(methodKey(row?.type));
}

function isAutoPayoutCapableForm(row) {
  if ((row?.provider || "manual") !== "manual") return false;
  if (!["cashout", "both"].includes(methodKey(row?.purpose))) return false;
  if (!isExplicitAutoPayoutName(row)) return false;
  return ["chime", "cashapp", "ecashapp"].includes(methodKey(row?.type));
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}
